import {
    Alert, Box, Stack, Typography, alpha,
    useMediaQuery, useTheme
} from '@mui/material';

import { getMondayFirstMonthOffset } from '../../../../helpers/admin/dashboard/dashboardHelper';
import DashboardOrdersExportButton from '../components/DashboardOrdersExportButton';

const WEEK_DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const DailyAverageTicketCalendar = ({
    rows,
    year,
    month,
    selectedDay,
    onSelectDay,
    operationLabel,
    segment,
    range,
    exportFilename
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const activeRows = rows.filter(row => row.count > 0);
    const maxAverage = Math.max(...rows.map(row => row.average), 0);
    const startOffset = getMondayFirstMonthOffset(year, month);
    const getOperationText = count => count === 1
        ? operationLabel.singular
        : operationLabel.plural;

    const dayButtonSx = (row) => {
        const intensity = maxAverage > 0 ? row.average / maxAverage : 0;
        const hasActivity = row.count > 0;
        const isSelected = row.day === selectedDay;

        return {
            appearance: 'none',
            borderRadius: 1.5,
            border: isSelected
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.divider}`,
            bgcolor: hasActivity
                ? alpha(theme.palette.primary.main, 0.07 + (intensity * 0.16))
                : theme.palette.grey[50],
            color: 'text.primary',
            cursor: 'pointer',
            minHeight: 104,
            p: 1.25,
            textAlign: 'left',
            transition: 'border-color 150ms ease, background-color 150ms ease, transform 150ms ease',
            width: '100%',
            '&:hover': {
                borderColor: 'primary.main',
                bgcolor: hasActivity
                    ? alpha(theme.palette.primary.main, 0.2)
                    : theme.palette.grey[100],
                transform: 'translateY(-1px)'
            },
            '&:focus-visible': {
                outline: `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                outlineOffset: 1
            }
        };
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h6">Calendario mensual</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Selecciona un día para consultar su detalle.
                    </Typography>
                </Box>
                <DashboardOrdersExportButton
                    reportType="average_ticket"
                    range={range}
                    segment={segment}
                    filename={exportFilename}
                />
            </Stack>

            {isMobile ? (
                activeRows.length > 0 ? (
                    <Stack spacing={1}>
                        {activeRows.map(row => (
                            <Box
                                component="button"
                                type="button"
                                key={row.day}
                                onClick={() => onSelectDay(row.day)}
                                aria-label={`${row.date}: ${row.formattedAverage}, ${row.count} ${getOperationText(row.count)}`}
                                sx={{
                                    ...dayButtonSx(row),
                                    minHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2
                                }}
                            >
                                <Box>
                                    <Typography component="span" fontWeight={700}>
                                        {row.date}
                                    </Typography>
                                    <Typography component="span" display="block" variant="body2" color="text.secondary">
                                        {row.count} {getOperationText(row.count)}
                                    </Typography>
                                </Box>
                                <Typography component="span" fontWeight={700} color="primary.main">
                                    {row.formattedAverage}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Alert severity="info">Este mes no tiene movimientos.</Alert>
                )
            ) : (
                <Box role="grid" aria-label="Ticket promedio por día del mes">
                    <Box
                        role="row"
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                            gap: 1,
                            mb: 1
                        }}
                    >
                        {WEEK_DAYS.map(dayName => (
                            <Typography
                                role="columnheader"
                                key={dayName}
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                fontWeight={700}
                            >
                                {dayName}
                            </Typography>
                        ))}
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                            gap: 1
                        }}
                    >
                        {Array.from({ length: startOffset }, (_, index) => (
                            <Box key={`empty-${index}`} aria-hidden="true" />
                        ))}
                        {rows.map(row => (
                            <Box
                                component="button"
                                type="button"
                                role="gridcell"
                                key={row.day}
                                onClick={() => onSelectDay(row.day)}
                                aria-selected={row.day === selectedDay}
                                aria-label={`${row.date}: ${row.formattedAverage}, ${row.count} ${getOperationText(row.count)}`}
                                sx={dayButtonSx(row)}
                            >
                                <Typography component="span" display="block" variant="caption" fontWeight={700}>
                                    {row.day}
                                </Typography>
                                {row.count > 0 ? (
                                    <>
                                        <Typography component="span" display="block" fontWeight={700} color="primary.main">
                                            {row.formattedAverage}
                                        </Typography>
                                        <Typography component="span" display="block" variant="caption" color="text.secondary">
                                            {row.count} {getOperationText(row.count)}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography component="span" display="block" variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                                        Sin movimientos
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DailyAverageTicketCalendar;
